// src/components/zoom/ZoomApiConnectionsModal.jsx
import React, { useState } from 'react';
import { FiX, FiLink, FiRefreshCw, FiCheckCircle, FiAlertTriangle, FiEdit2, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { saveZoomConnection, listZoomUsers, zoomErrorMessage } from '../../services/zoomApiService';

const EMPTY = { id: null, label: '', zoomAccountId: '', clientId: '', clientSecret: '' };

const ZoomApiConnectionsModal = ({ connections, onClose }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await saveZoomConnection({ ...form, clientSecret: form.clientSecret || undefined });
      toast.success(`Conexión guardada · ${r.userCount} usuarios de Zoom`);
      setForm(null);
    } catch (err) {
      toast.error(zoomErrorMessage(err), { duration: 7000 });
    } finally { setSaving(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await listZoomUsers(); toast.success('Usuarios actualizados'); }
    catch (err) { toast.error(zoomErrorMessage(err)); }
    finally { setRefreshing(false); }
  };

  const input = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FiLink /> Conexión Zoom API</h3>
            <p className="text-xs text-gray-500 mt-0.5">Apps Server-to-Server de marketplace.zoom.us. El secreto nunca vuelve al navegador.</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FiX size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {connections.length === 0 && !form && (
            <p className="text-sm text-gray-500 text-center py-6">Aún no hay conexiones. Agrega la cuenta de 5 licencias y la personal.</p>
          )}
          {connections.map(c => (
            <div key={c.id} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 flex items-center gap-2">
                    {c.status === 'error' ? <FiAlertTriangle className="text-red-500" /> : <FiCheckCircle className="text-green-500" />}
                    {c.label} <span className="text-xs font-normal text-gray-400">{c.zoomAccountIdMasked}</span>
                  </p>
                  {c.status === 'error' && <p className="text-xs text-red-600 mt-1">{c.lastError}</p>}
                </div>
                <button onClick={() => setForm({ ...EMPTY, id: c.id, label: c.label })}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar credenciales"><FiEdit2 size={14} /></button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(c.users || []).map(u => (
                  <span key={u.email} className={`text-[11px] px-2 py-0.5 rounded-full ${u.type === 1 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}
                    title={u.type === 1 ? 'Básico (40 min) — no se usa para crear salas automáticas' : 'Licenciado'}>
                    {u.email}{u.type === 1 ? ' · básico' : ''}
                  </span>
                ))}
              </div>
            </div>
          ))}

          {form && (
            <form onSubmit={handleSave} className="border border-blue-200 bg-blue-50/40 rounded-xl p-4 space-y-3">
              <input className={input} placeholder="Nombre (ej. Hingenia 5 licencias)" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} required />
              <input className={input} placeholder={form.id ? 'Account ID (vacío = conservar el actual)' : 'Account ID'} value={form.zoomAccountId} onChange={e => setForm({ ...form, zoomAccountId: e.target.value })} required={!form.id} />
              <input className={input} placeholder={form.id ? 'Client ID (vacío = conservar el actual)' : 'Client ID'} value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} required={!form.id} />
              <input className={input} type="password" autoComplete="new-password"
                placeholder={form.id ? 'Client Secret (vacío = conservar el actual)' : 'Client Secret'}
                value={form.clientSecret} onChange={e => setForm({ ...form, clientSecret: e.target.value })} required={!form.id} />
              <p className="text-[11px] text-gray-500">Permisos en la app: meeting:write:admin, meeting:read:admin, user:read:admin (o los granulares meeting:write:meeting:admin, meeting:read:meeting:admin, user:read:user:admin).</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setForm(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
                  {saving ? 'Probando conexión…' : 'Probar y guardar'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-100">
          <button onClick={handleRefresh} disabled={refreshing || connections.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-40">
            <FiRefreshCw size={14} className={refreshing ? 'animate-spin' : ''} /> Actualizar usuarios
          </button>
          {!form && (
            <button onClick={() => setForm({ ...EMPTY })} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
              <FiPlus size={14} /> Agregar conexión
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZoomApiConnectionsModal;
